/**
 * Dev-only attachment scope / grouped-view tracer (#8070).
 *
 * Web: append `?attachmentScopeDebug=1` to Create Request or Request Detail URL.
 * Console: `window.__attachmentScopeDebugDump()` prints the last snapshot per label.
 */

export const ATTACHMENT_SCOPE_DEBUG_PARAM = 'attachmentScopeDebug';

type DebugSnapshot = {
  at: string;
  label: string;
  data: Record<string, unknown>;
};

const MAX_SNAPSHOTS = 40;
const snapshots: DebugSnapshot[] = [];

export function isAttachmentScopeDebugEnabled(): boolean {
  if (typeof __DEV__ === 'undefined' || !__DEV__) {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return (
      new URLSearchParams(window.location.search).get(
        ATTACHMENT_SCOPE_DEBUG_PARAM,
      ) === '1'
    );
  } catch {
    return false;
  }
}

function summarizeAttachmentsBySection(
  attachmentsBySection: Record<string, unknown> | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [sectionId, raw] of Object.entries(attachmentsBySection ?? {})) {
    if (Array.isArray(raw)) {
      out[sectionId] = raw.length;
    } else if (raw != null) {
      out[sectionId] = 1;
    }
  }
  return out;
}

export function logAttachmentViewToggle(
  label: string,
  context: Record<string, unknown>,
  source?: unknown,
): void {
  if (
    typeof __DEV__ !== 'undefined' &&
    __DEV__ &&
    typeof window !== 'undefined'
  ) {
    console.warn(`[attachment-view] ${label}`, context);
    if (source != null) {
      console.warn('[attachment-view] source object', source);
    }
  }
  debugAttachmentScope(label, context, source);
}

export function debugAttachmentScope(
  label: string,
  data: Record<string, unknown>,
  source?: unknown,
): void {
  if (!isAttachmentScopeDebugEnabled()) {
    return;
  }

  const normalized: Record<string, unknown> = { ...data };
  if (
    data.attachmentsBySection &&
    typeof data.attachmentsBySection === 'object'
  ) {
    normalized.attachmentsBySectionCounts = summarizeAttachmentsBySection(
      data.attachmentsBySection as Record<string, unknown>,
    );
    delete normalized.attachmentsBySection;
  }
  if (
    data.rawAttachmentsBySection &&
    typeof data.rawAttachmentsBySection === 'object'
  ) {
    normalized.rawAttachmentsBySectionCounts = summarizeAttachmentsBySection(
      data.rawAttachmentsBySection as Record<string, unknown>,
    );
    delete normalized.rawAttachmentsBySection;
  }
  if (
    data.scopedAttachmentsBySection &&
    typeof data.scopedAttachmentsBySection === 'object'
  ) {
    normalized.scopedAttachmentsBySectionCounts = summarizeAttachmentsBySection(
      data.scopedAttachmentsBySection as Record<string, unknown>,
    );
    delete normalized.scopedAttachmentsBySection;
  }

  const entry: DebugSnapshot = {
    at: new Date().toISOString(),
    label,
    data: {
      ...normalized,
      hasSourceObject: source != null,
    },
  };
  snapshots.push(entry);
  if (snapshots.length > MAX_SNAPSHOTS) {
    snapshots.shift();
  }

  console.groupCollapsed(`[attachment-scope] ${label}`);
  console.log('context', normalized);
  if (source != null) {
    console.log('source object', source);
  }
  console.groupEnd();
}

export function getAttachmentScopeDebugSnapshots(): readonly DebugSnapshot[] {
  return snapshots;
}

export function dumpAttachmentScopeDebugToConsole(): void {
  if (!isAttachmentScopeDebugEnabled()) {
    console.warn(
      `[attachment-scope] Enable with ?${ATTACHMENT_SCOPE_DEBUG_PARAM}=1 in the URL`,
    );
    return;
  }
  console.table(
    snapshots.map(s => ({
      at: s.at,
      label: s.label,
      ...flattenForTable(s.data),
    })),
  );
  console.log('[attachment-scope] full snapshots:', snapshots);
}

function flattenForTable(
  data: Record<string, unknown>,
): Record<string, string> {
  const row: Record<string, string> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value == null) {
      row[key] = String(value);
    } else if (typeof value === 'object') {
      row[key] = JSON.stringify(value);
    } else {
      row[key] = String(value);
    }
  }
  return row;
}

declare global {
  interface Window {
    __attachmentScopeDebugDump?: () => void;
    __attachmentScopeDebugSnapshots?: () => readonly DebugSnapshot[];
  }
}

export function registerAttachmentScopeDebugGlobals(): void {
  if (typeof window === 'undefined' || !isAttachmentScopeDebugEnabled()) {
    return;
  }
  window.__attachmentScopeDebugDump = dumpAttachmentScopeDebugToConsole;
  window.__attachmentScopeDebugSnapshots = getAttachmentScopeDebugSnapshots;
  console.info(
    `[attachment-scope] Debug ON — call window.__attachmentScopeDebugDump()`,
  );
}
